import { Box, Button, Chip, Grid, Typography, useColorScheme } from "@mui/joy";
import { useMediaQuery, useTheme } from "@mui/material";
import { useState } from "react";
import { povCharacters } from "../data/characterData";
import FlipCard from "./FlipCard";

export default function SapphireShorePOVs() {
  const theme = useTheme();
  const { mode } = useColorScheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedRealm, setSelectedRealm] = useState<string>("All");
  const isDark = mode === "dark";

  // Extract unique realms from character data
  const realms = [
    "All",
    ...Array.from(new Set(povCharacters.map((char) => char.affiliation))),
  ];

  // Filter characters based on selected realm
  const filteredCharacters =
    selectedRealm === "All"
      ? povCharacters
      : povCharacters.filter((char) => char.affiliation === selectedRealm);

  // Get realm color for styling
  const getRealmColor = (realm: string) => {
    const realmLower = realm.toLowerCase();
    if (realmLower.includes("migora")) return "rgba(255, 215, 0, 0.6)";
    if (realmLower.includes("hydaria")) return "rgba(239, 68, 68, 0.6)";
    if (realmLower.includes("rigata")) return "rgba(34, 197, 94, 0.6)";
    if (realmLower.includes("vara")) return "rgba(168, 85, 247, 0.6)";
    if (realmLower.includes("riverlands")) return "rgba(59, 130, 246, 0.6)";
    if (realmLower.includes("gaudy")) return "rgba(236, 72, 153, 0.6)";
    if (realmLower.includes("osgara")) return "rgba(156, 163, 175, 0.6)";
    return "rgba(120, 119, 198, 0.6)";
  };

  return (
    <Box
      sx={{
        position: "relative",
        py: 6,
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          textAlign: "center",
          mb: 6,
          position: "relative",
        }}
      >
        {/* Magical glow effect behind title */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "500px",
            height: "120px",
            background: isDark
              ? "radial-gradient(ellipse, rgba(120, 119, 198, 0.1) 0%, transparent 70%)"
              : "radial-gradient(ellipse, rgba(120, 119, 198, 0.05) 0%, transparent 70%)",
            filter: "blur(30px)",
            zIndex: -1,
          }}
        />

        <Typography
          level={isMobile ? "h3" : "h2"}
          sx={{
            color: isDark ? "white" : "#1e293b",
            fontWeight: 300,
            letterSpacing: "0.05em",
            textShadow: isDark
              ? "0 0 20px rgba(120, 119, 198, 0.5)"
              : "0 0 10px rgba(120, 119, 198, 0.3)",
            mb: 2,
          }}
        >
          POV Characters
        </Typography>

        <Typography
          level="title-md"
          sx={{
            color: isDark
              ? "rgba(255, 255, 255, 0.7)"
              : "rgba(30, 41, 59, 0.7)",
            fontStyle: "italic",
            letterSpacing: "0.02em",
            mb: 4,
          }}
        >
          of The Sapphire Shore
        </Typography>

        {/* Decorative elements */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mb: 4,
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: "60px",
              height: "1px",
              background: isDark
                ? "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)"
                : "linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.3), transparent)",
            }}
          />
          <Box
            sx={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(120, 119, 198, 0.8) 0%, transparent 70%)",
              boxShadow: "0 0 10px rgba(120, 119, 198, 0.6)",
            }}
          />
          <Box
            sx={{
              width: "60px",
              height: "1px",
              background: isDark
                ? "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)"
                : "linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.3), transparent)",
            }}
          />
        </Box>
      </Box>

      {/* Realm Filter Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mb: 6,
          px: 2,
        }}
      >
        <Box
          sx={{
            background: isDark
              ? "rgba(0, 0, 0, 0.3)"
              : "rgba(255, 255, 255, 0.6)",
            border: isDark
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.1)",
            borderRadius: "16px",
            p: 3,
            backdropFilter: "blur(10px)",
            maxWidth: "100%",
            overflow: "hidden",
          }}
        >
          <Typography
            level="title-sm"
            sx={{
              color: isDark ? "white" : "#1e293b",
              mb: 2,
              textAlign: "center",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontSize: "0.8rem",
            }}
          >
            Filter by Realm
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              justifyContent: "center",
              maxWidth: isMobile ? "300px" : "800px",
            }}
          >
            {realms.map((realm) => (
              <Chip
                key={realm}
                variant={selectedRealm === realm ? "solid" : "outlined"}
                onClick={() => setSelectedRealm(realm)}
                sx={{
                  cursor: "pointer",
                  background:
                    selectedRealm === realm
                      ? `linear-gradient(45deg, ${getRealmColor(
                          realm
                        )}, ${getRealmColor(realm)}cc)`
                      : isDark
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.05)",
                  border: `1px solid ${
                    selectedRealm === realm
                      ? getRealmColor(realm)
                      : isDark
                      ? "rgba(255, 255, 255, 0.2)"
                      : "rgba(0, 0, 0, 0.2)"
                  }`,
                  color:
                    selectedRealm === realm
                      ? "white"
                      : isDark
                      ? "white"
                      : "#1e293b",
                  fontWeight: selectedRealm === realm ? 600 : 400,
                  fontSize: "0.75rem",
                  fontFamily: "'Cinzel', serif",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background:
                      selectedRealm === realm
                        ? `linear-gradient(45deg, ${getRealmColor(
                            realm
                          )}, ${getRealmColor(realm)}cc)`
                        : isDark
                        ? "rgba(255, 255, 255, 0.1)"
                        : "rgba(0, 0, 0, 0.1)",
                    border: `1px solid ${getRealmColor(realm)}`,
                    transform: "translateY(-1px)",
                  },
                }}
                size="sm"
              >
                {realm}
              </Chip>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Character Count */}
      <Box
        sx={{
          textAlign: "center",
          mb: 4,
        }}
      >
        <Typography
          level="body-sm"
          sx={{
            color: isDark
              ? "rgba(255, 255, 255, 0.6)"
              : "rgba(30, 41, 59, 0.6)",
            fontStyle: "italic",
          }}
        >
          Showing {filteredCharacters.length} character
          {filteredCharacters.length !== 1 ? "s" : ""}
          {selectedRealm !== "All" && ` from ${selectedRealm}`}
        </Typography>
      </Box>

      {/* Characters Grid */}
      <Box
        sx={{
          maxWidth: "1400px",
          margin: "0 auto",
          px: { xs: 2, sm: 3, md: 4, lg: 6 },
        }}
      >
        <Grid
          container
          spacing={{ xs: 3, sm: 4, md: 5 }}
          sx={{
            justifyContent: "center",
          }}
        >
          {filteredCharacters.map((character) => (
            <Grid
              key={character.id}
              xs={12}
              sm={6}
              md={4}
              lg={3}
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <FlipCard character={character} />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* No characters message */}
      {filteredCharacters.length === 0 && (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
          }}
        >
          <Typography
            level="body-lg"
            sx={{
              color: isDark
                ? "rgba(255, 255, 255, 0.6)"
                : "rgba(30, 41, 59, 0.6)",
              fontStyle: "italic",
            }}
          >
            No characters found for the selected realm.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setSelectedRealm("All")}
            sx={{
              mt: 2,
              background: isDark
                ? "rgba(255, 255, 255, 0.05)"
                : "rgba(0, 0, 0, 0.05)",
              border: isDark
                ? "1px solid rgba(255, 255, 255, 0.2)"
                : "1px solid rgba(0, 0, 0, 0.2)",
              color: isDark ? "white" : "#1e293b",
              "&:hover": {
                background: isDark
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.1)",
              },
            }}
          >
            Show All Characters
          </Button>
        </Box>
      )}
    </Box>
  );
}
